import { DivisionService } from '../services/DivisionService';
import { tokenService } from '../../../shared/services/auth/tokenService';
import { resetDivisionScopedStores } from '../../../store/resetAllStores';

const TOKEN_REFRESH_FAILED_MARKER = 'token refresh failed';

// sessionStorage key used to carry the division name across the hard reload below — React
// state doesn't survive a full page navigation, but sessionStorage does (for the same tab).
// AppSuspenseFallback reads this to show DivisionSwitchLoader instead of the generic
// PostLoginLoader while the reloaded page's lazy chunks are still booting, so the whole
// switch reads as one continuous animation instead of two different loaders back to back.
export const DIVISION_SWITCH_STORAGE_KEY = 'divisionSwitchInProgress';

/**
 * Switches the current session into another division: refreshes tokens, updates the user
 * store, clears every division-scoped store/cache, then navigates to /dashboard — the same
 * flow the sidebar's DivisionSwitcher uses, and mirrors what happens right after login.
 * Shared so any "switch into this division" entry point (the switcher, or a toast prompting
 * a user who just added themself to a division) behaves identically.
 *
 * @param {string} divId - Division to switch into
 * @param {(data: object) => void} setUserData - useUser().setUserData
 * @returns {Promise<void>} Resolves only on failure (success navigates away instead)
 * @throws {Error} with a user-facing `.message` on failure
 */
export const switchDivisionSession = async (divId, setUserData) => {
  try {
    const data = await DivisionService.switchDivision(divId);

    tokenService.setTokens(data.access_token, data.id_token, data.refresh_token);
    // accessToken/idToken must also go into userData itself (not just tokenService's own
    // storage) — userData is what gets persisted and re-read by userStore's hydration on
    // the next page load (see the hard navigation below). Without this, hydration would
    // reload the OLD division's tokens from the stale persisted userData and clobber the
    // fresh ones tokenService.setTokens() just wrote, right after this switch completes.
    setUserData({
      divisionId: data.divisionId,
      divisionRole: data.divisionRole,
      conversationsBucket: data.conversationsBucket,
      fileManagementBucket: data.fileManagementBucket,
      accessToken: data.access_token,
      idToken: data.id_token
    });

    // Stash the division name so AppSuspenseFallback can show DivisionSwitchLoader (instead
    // of the generic PostLoginLoader) during the brief window right after the reload below
    // where the new page's lazy chunks are still booting — see DIVISION_SWITCH_STORAGE_KEY.
    // Best-effort: sessionStorage can throw in some privacy modes, and losing this is only
    // cosmetic (the fallback just falls back to PostLoginLoader).
    try {
      if (data.divisionName) {
        sessionStorage.setItem(DIVISION_SWITCH_STORAGE_KEY, data.divisionName);
      }
    } catch (storageError) {
      // Non-fatal — see comment above.
    }

    // Clear every store/cache with division-scoped data BEFORE navigating — several of
    // these (runStore, generationStore, dashboardStore, tokenUsageStore) persist to
    // localStorage, so a reload alone would just rehydrate the old division's data right
    // back. Landing on /dashboard (a full navigation, not just a reload) mirrors what
    // happens after login, and guarantees every component remounts and refetches under
    // the new division/tokens instead of relying on each page's own effects to notice the
    // reset and refetch on their own — including pages that don't make sense to view under
    // the new division (e.g. a project the user no longer has access to).
    resetDivisionScopedStores();
    if (window.location.pathname === '/dashboard') {
      // Already there — assigning the same path is a no-op in some browsers, so force
      // the reload explicitly instead.
      window.location.reload();
    } else {
      window.location.href = '/dashboard';
    }
  } catch (err) {
    // Backend error bodies aren't consistently shaped (plain string vs {message}) across
    // the different exception types switchCurrentDivision can throw — check both.
    const message = err?.response?.data?.message || (typeof err?.response?.data === 'string' ? err.response.data : '') || err.message || '';
    const status = err?.response?.status;

    if (message.toLowerCase().includes(TOKEN_REFRESH_FAILED_MARKER)) {
      throw new Error('Division changed, but your session could not be refreshed. Please log in again for it to take full effect.');
    }
    if (status === 403) {
      throw new Error('You are not a member of that Division.');
    }
    if (status === 404) {
      throw new Error('That Division no longer exists. It may have been deleted.');
    }
    if (status === 401) {
      throw new Error('Your session has expired. Please log in again.');
    }
    if (!err?.response) {
      // No response at all — the request never completed (offline, timeout, CORS,
      // backend unreachable), as opposed to a response that came back with an error status.
      throw new Error('Could not reach the server to switch Division. Check your connection and try again.');
    }
    if (status >= 500) {
      throw new Error('Division switching is temporarily unavailable. Please try again shortly.');
    }
    throw new Error('Failed to switch Division. Please try again.');
  }
};
